import { logger } from "@/lib/logger";
import type { MapPlace } from "@/types/places";
import { type MapBounds, isWithinBounds } from "@/utils/mapQueryHelpers";
import { create } from "zustand";

export type MarkerAnimationTrigger = "search" | "zoom" | "filter" | "hover";

/**
 * Zustand store for map UI state.
 * Consolidates scattered state from MapView into single source of truth.
 */
interface MapState {
  // Selection state
  selectedPlace: MapPlace | null;
  selectedCluster: MapPlace[] | null;
  hoveredPlace: MapPlace | null;

  // Map instance and viewport
  mapInstance: google.maps.Map | null;
  bounds: MapBounds | null;
  /** Where a user move ended, until the debounced `bounds` catch up. */
  settlingBounds: MapBounds | null;

  // Animation state
  animationTrigger: MarkerAnimationTrigger;

  /** searchAreaKey of the search the user moved the map in; the list follows the map while it still matches. */
  viewportSearchKey: string | null;

  // Actions
  selectPlace: (place: MapPlace | null) => void;
  selectCluster: (places: MapPlace[] | null) => void;
  hoverPlace: (place: MapPlace | null) => void;
  setMapInstance: (map: google.maps.Map | null) => void;
  setBounds: (bounds: MapBounds | null) => void;
  setSettlingBounds: (bounds: MapBounds | null) => void;
  setAnimationTrigger: (trigger: MarkerAnimationTrigger) => void;
  followViewport: (searchKey: string) => void;
  resetViewport: () => void;
  clearSelection: () => void;
}

function isPlaceWithinBounds(
  place: MapPlace,
  bounds: MapBounds | null,
): boolean {
  return bounds !== null && isWithinBounds(place.coords, bounds);
}

/**
 * Map state store using Zustand.
 * Provides centralized state management for map UI.
 *
 * @example
 * ```tsx
 * const { selectedPlace, selectPlace } = useMapStore();
 *
 * // Select a place
 * selectPlace(mapPlace);
 *
 * // Clear selection
 * selectPlace(null);
 * ```
 */
export const useMapStore = create<MapState>((set, get) => ({
  // Initial state
  selectedPlace: null,
  selectedCluster: null,
  hoveredPlace: null,
  mapInstance: null,
  bounds: null,
  settlingBounds: null,
  animationTrigger: "search",
  viewportSearchKey: null,

  /**
   * Select a place on the map.
   * Clears cluster selection and tracks analytics event.
   */
  selectPlace: (place) => {
    set({ selectedPlace: place, selectedCluster: null });

    // Track analytics event
    if (place) {
      try {
        // Analytics tracking would go here
        logger.info(
          {
            placeId: place.id,
            placeType: place.type,
          },
          "Map place selected",
        );
      } catch (error) {
        logger.error({ error }, "Failed to track map place selection");
      }
    }
  },

  /**
   * Select a cluster of places on the map.
   * Clears single place selection.
   */
  selectCluster: (places) => {
    set({ selectedCluster: places, selectedPlace: null });

    if (places && places.length > 0) {
      logger.info(
        {
          clusterSize: places.length,
          placeType: places[0].type,
        },
        "Map cluster selected",
      );
    }
  },

  /**
   * Hover over a place on the map.
   * Validates that the place is within the current viewport bounds.
   * If bounds are not set or place is outside viewport, hover is rejected.
   * Passing null always clears the hover state.
   */
  hoverPlace: (place) => {
    // Allow clearing hover
    if (place === null) {
      set({ hoveredPlace: null });
      return;
    }

    // Validate place is within viewport bounds
    const { bounds } = get();
    if (!bounds || !isPlaceWithinBounds(place, bounds)) {
      return;
    }

    set({ hoveredPlace: place });
  },

  /**
   * Set the Google Maps instance.
   * Called after map initialization.
   */
  setMapInstance: (map) => set({ mapInstance: map }),

  /**
   * Update the current map viewport bounds.
   * Called when map bounds change (zoom, pan, etc.).
   * Clears hover state if currently hovered place is now outside viewport.
   */
  setBounds: (bounds) => {
    const currentHovered = get().hoveredPlace;
    // Clear hover if currently hovered place is now outside bounds
    if (currentHovered && !isPlaceWithinBounds(currentHovered, bounds)) {
      set({ bounds, hoveredPlace: null });
    } else {
      set({ bounds });
    }
  },

  setSettlingBounds: (settlingBounds) =>
    set((state) =>
      state.settlingBounds === settlingBounds ? state : { settlingBounds },
    ),

  /**
   * Set the animation trigger for markers.
   * Controls which animation type is applied to markers when they appear.
   */
  setAnimationTrigger: (trigger) => set({ animationTrigger: trigger }),

  followViewport: (searchKey) => {
    if (get().viewportSearchKey !== searchKey) {
      set({ viewportSearchKey: searchKey });
    }
  },

  resetViewport: () => set({ viewportSearchKey: null }),

  /**
   * Clear all selections (place and cluster).
   * Useful when clicking on the map background.
   */
  clearSelection: () =>
    set({
      selectedPlace: null,
      selectedCluster: null,
    }),
}));
